import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  groupByPost, engageQueueState,
  repeatsLabel,
  whyItsHere,
  draftReply,
  sortGroups,
  type EngageLead,
  type EngageSort,
} from "../../../lib/engageGrouping";
import { ageLabel } from "../../../lib/reelIdentity";
import SocialsListState from "../SocialsListState";
import DismissDialog from "./DismissDialog";
import SocialsCard from "../SocialsCard";

/**
 * Ch94 · ENGAGE — Reddit and Instagram in ONE list.
 *
 * R23: posting reels, replying on Reddit and Instagram actions are ONE machine
 * and stay together. This is the replying half.
 *
 * 🔴 WHAT THE LIVE QUEUE LOOKED LIKE (Ch94 Director, 2026-09-19): 99 to reply
 * to, 0 ever replied, the same post 10+ times, every drafted reply EMPTY, and
 * Instagram showing system prose — "NO ROSTER RUN YET … edge is UNKNOWN".
 *
 * Four things change here: repeats collapse into one row · the reason a post is
 * here is said in Grant's words with the scorer's line behind a disclosure · an
 * empty draft SAYS it is empty instead of presenting a blank box as a
 * suggestion · Dismiss is a real dialog, not `window.prompt`.
 */

export type EngageState = "needs_reply" | "replied" | "dismissed";

export interface EngageAction {
  action: "copied" | "marked_replied" | "dismissed";
  leadId: string;
  finalText: string;
  suggestedText: string;
  dismissReason?: string;
}

interface Props {
  leads: EngageLead[] | null;
  initialLead?: string | null;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  busyId?: string | null;
  state: EngageState;
  onStateChange: (state: EngageState) => void;
  counts?: Partial<Record<EngageState, number>>;
  onAction: (action: EngageAction) => void;
  /** Prefills the EXISTING Add Client flow. Absent ⇒ the control is not offered. */
  onTurnIntoLead?: (lead: EngageLead) => void;
}

/*
 * 🔴 THE WORD IS "DISMISSED", AND THIS PILL WAS THE ONE THAT KEPT SAYING "SKIPPED".
 *
 * Grant's word is Dismiss. The button (:277) and the dialog were changed to it, and
 * the review pack's README records the change as done — which is exactly why nobody
 * re-checked this line. The filter pill is the same word in the same tab; one
 * instance was fixed and the class was never swept.
 *
 * The STATE KEY stays `dismissed` — it is the wire value and is not Grant's to read.
 */
const STATE_LABELS: Array<[EngageState, string]> = [
  ["needs_reply", "To do"],
  ["replied", "Done"],
  ["dismissed", "Dismissed"],
];

const titleOf = (lead: EngageLead): string => {
  const raw = (lead.raw_payload ?? {}) as { title?: unknown };
  return String(raw.title ?? lead.body_preview ?? "A Reddit post");
};

const subredditOf = (lead: EngageLead): string => {
  const raw = (lead.raw_payload ?? {}) as { subreddit?: unknown };
  return String(raw.subreddit ?? "Reddit");
};

const EngageTab: React.FC<Props> = ({
  leads, initialLead, loading, error, onRetry, busyId,
  state,
  onStateChange,
  counts,
  onAction,
  onTurnIntoLead,
}) => {
  const [showAll, setShowAll] = React.useState(false);
  const [sort, setSort] = React.useState<EngageSort>("newest");
  const [drafts, setDrafts] = React.useState<Record<string, string>>({});
  const [showRaw, setShowRaw] = React.useState<Record<string, boolean>>({});
  const [dismissing, setDismissing] = React.useState<EngageLead | null>(null);

  const groups = React.useMemo(
    () => sortGroups(groupByPost(leads ?? []).filter(group => {
      const selected = initialLead && group.repeats.find(lead => lead.id === initialLead);
      if (selected) return true;
      const current = engageQueueState(group.lead);
      return current === state;
    }), sort),
    [leads, sort, state, initialLead]
  );

  // A linked conversation must remain reachable even beyond the collapsed list.
  const linkedOutsidePreview = groups.slice(10).some(group =>
    group.repeats.some(lead => lead.id === initialLead));
  const expanded = showAll || linkedOutsidePreview;
  const visibleGroups = expanded ? groups : groups.slice(0, 10);

  React.useEffect(() => {
    if (initialLead) document.getElementById(`engage-${initialLead}`)?.scrollIntoView?.({block:"center"});
  }, [initialLead, leads]);

  return (
    <Stack spacing={2}>
      <Stack direction="row" gap={1} flexWrap="wrap" data-testid="engage-states">
        {STATE_LABELS.map(([key, label]) => {
          const count = counts?.[key];
          return (
            <Chip
              key={key}
              // A count renders only when we HAVE one. A missing count must not
              // become a confident 0 next to work he has not done.
              label={typeof count === "number" ? `${label} ${count}` : label}
              variant={state === key ? "filled" : "outlined"}
              color={state === key ? "primary" : "default"}
              sx={{minHeight:44}}
              onClick={() => onStateChange(key)}
            />
          );
        })}
      </Stack>

      <Stack direction="row" gap={0.75}>
        {(
          [
            ["newest", "Newest first"],
            ["hottest", "Hottest first"],
          ] as Array<[EngageSort, string]>
        ).map(([value, label]) => (
          <Chip
            key={value}
            size="small"
            label={label}
            variant={sort === value ? "filled" : "outlined"}
            sx={{minHeight:44}}
            onClick={() => setSort(value)}
          />
        ))}
      </Stack>

      {loading || error || leads === null ? <SocialsListState loading={loading} error={error} onRetry={onRetry}
        items={leads} testIdPrefix="engage" emptyMessage="Nothing here right now." unreadMessage="Conversations haven't been read yet." /> : null}
      {!loading && !error && leads !== null && initialLead && !leads.some(lead => lead.id === initialLead) ? (
        <Alert severity="warning">This conversation is not in the list we can load. Only the newest 100 HOT or MAY conversations are available here; this link may be older or outside those filters. Refresh cannot retrieve a conversation outside that window.</Alert>
      ) : null}
      {!loading && !error && leads !== null && groups.length === 0 ? (
        <Typography color="text.secondary" data-testid="engage-empty">
          Nothing here right now.
        </Typography>
      ) : null}

      {!loading && !error && visibleGroups.map((group) => {
        const lead = group.repeats.find(candidate => candidate.id === initialLead) ?? group.lead;
        const why = whyItsHere(lead);
        const draft = draftReply(lead);
        const repeats = repeatsLabel(group);
        const value = drafts[lead.id] ?? draft.text;

        return (
          <Box
            key={group.permalink}
            id={`engage-${lead.id}`}
            data-testid={lead.id === initialLead ? "engage-selected-conversation" : "engage-row"}
            sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 2 }}
          >
            <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
              <Chip size="small" label={subredditOf(lead)} />
              <Chip
                size="small"
                color={lead.verdict === "HOT" ? "warning" : "default"}
                label={lead.verdict}
              />
              {repeats ? (
                // Kept, not discarded: a post the scorer keeps re-surfacing is
                // evidence about the post.
                <Chip size="small" variant="outlined" label={repeats} data-testid="engage-repeats" />
              ) : null}
              {/*
                HOW OLD THE POST IS — the mock carries it on every row, and it is
                the field that decides whether replying is still worth doing.
                `created_at` is non-optional on EngageLead (engageGrouping.ts:20),
                so this renders for every row; ageLabel still returns null on an
                unparseable value and we then show nothing rather than a guess.

                The mock also shows a COMMENT COUNT ("38 comments"). That is NOT
                on this contract — EngageLead has no such field and the payload is
                not known to carry one — so it is deliberately absent rather than
                invented. Filed, not faked.
              */}
              {ageLabel(lead.created_at) ? (
                <Typography variant="caption" color="text.secondary" data-testid="engage-age">
                  {ageLabel(lead.created_at)}
                </Typography>
              ) : null}
            </Stack>

            <Typography variant="subtitle1" sx={{ mt: 1 }}>
              {titleOf(lead)}
            </Typography>
            {/*
              🔴 THE SAME STRING, TWICE. `titleOf` falls back to body_preview when
              the payload carries no title, and this line then printed that exact
              string again directly underneath it.

              Only the IDENTICAL case is suppressed. On the real wire body_preview
              is the post's BODY and is legitimately different from its title, so
              anything cleverer — collapsing near-duplicates, prefixes, one
              containing the other — would start hiding real text Grant needs.
              (The engage fixture makes this look worse than it is: its
              body_preview is a paraphrase of its own title, which is not the shape
              the wire delivers.)
            */}
            {lead.body_preview && lead.body_preview !== titleOf(lead) ? (
              <Typography variant="body2" color="text.secondary">
                {lead.body_preview}
              </Typography>
            ) : null}

            {/* WHY IT'S HERE — his words, with the scorer's line tucked behind a link. */}
            {why.plain.length > 0 || why.untranslated.length > 0 || why.raw ? (
              <Box sx={{ mt: 1 }} data-testid="engage-why">
                {why.plain.length > 0 ? (
                  <Typography variant="body2">
                    Why it's here: {why.plain.join(" · ")}
                  </Typography>
                ) : null}
                {why.untranslated.length > 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    Also flagged: {why.untranslated.join(" · ")}
                  </Typography>
                ) : null}
                {why.raw ? (
                  <Box>
                    <Button
                      size="small"
                      onClick={() =>
                        setShowRaw((prev) => ({ ...prev, [lead.id]: !prev[lead.id] }))
                      }
                      data-testid="engage-raw-toggle"
                    >
                      {showRaw[lead.id] ? "Hide the scoring line" : "Show the scoring line"}
                    </Button>
                    {showRaw[lead.id] ? (
                      <Typography
                        variant="caption"
                        component="div"
                        color="text.secondary"
                        data-testid="engage-raw"
                      >
                        {why.raw}
                      </Typography>
                    ) : null}
                  </Box>
                ) : null}
              </Box>
            ) : null}

            {/*
              🔴 AN EMPTY DRAFT SAYS SO. A blank box under the label "Drafted
              reply" reads as "the machine suggests nothing here", which is a
              claim about the reply rather than about the pipeline. Every box on
              the live queue was blank on 2026-09-19.
            */}
            {draft.source === "none" ? (
              <Alert severity="info" sx={{ mt: 1.5 }} data-testid="engage-no-draft">
                No reply was drafted for this one — the words below are yours.
              </Alert>
            ) : null}

            <TextField
              fullWidth
              multiline
              minRows={4}
              sx={{ mt: 1 }}
              label={
                draft.source === "none"
                  ? "Your reply"
                  : "Drafted reply · make it yours before posting"
              }
              value={value}
              onChange={(event) =>
                setDrafts((prev) => ({ ...prev, [lead.id]: event.target.value }))
              }
              data-testid="engage-reply"
            />

            <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 1.5 }}>
              <Button
                component="a" href={/^https?:\/\//.test(lead.source_url) ? lead.source_url : undefined} target="_blank" rel="noopener noreferrer"
                variant="contained"
                onClick={() =>
                  onAction({
                    action: "copied",
                    leadId: lead.id,
                    finalText: value,
                    suggestedText: draft.text,
                  })
                }
                disabled={busyId === lead.id || engageQueueState(lead) !== "needs_reply"}
                data-testid="engage-copy"
              >
                Copy and open post
              </Button>
              <Button
                onClick={() =>
                  onAction({
                    action: "marked_replied",
                    leadId: lead.id,
                    finalText: value,
                    suggestedText: draft.text,
                  })
                }
                disabled={busyId === lead.id || engageQueueState(lead) !== "needs_reply"}
                data-testid="engage-replied"
              >
                Mark replied
              </Button>
              {engageQueueState(lead) !== "needs_reply" ? <Button component="a" href={/^https?:\/\//.test(lead.source_url) ? lead.source_url : undefined} target="_blank" rel="noopener noreferrer">Open post</Button> : null}
              {onTurnIntoLead ? (
                <Button onClick={() => onTurnIntoLead(lead)} data-testid="engage-to-lead">
                  Turn into a lead
                </Button>
              ) : null}
              <Button
                color="error"
                onClick={() => setDismissing(lead)}
                disabled={busyId === lead.id || engageQueueState(lead) !== "needs_reply"}
                data-testid="engage-dismiss"
              >
                Dismiss
              </Button>
            </Stack>
          </Box>
        );
      })}

      {!loading && !error && !expanded && groups.length > 10 ? (
        <Button onClick={() => setShowAll(true)} sx={{ alignSelf: "flex-start", minHeight: 44 }}>
          Show all ({groups.length})
        </Button>
      ) : null}

      {/*
        INSTAGRAM — one honest card. No dead filter chips: the old desk showed
        chips the backend ignores entirely, which is a control that lies about
        having an effect.
      */}
      <SocialsCard title="Instagram" state="not_connected" testId="engage-instagram">
        <Typography variant="body2" color="text.secondary">
          This will list people worth a like, a comment or a message: accounts that follow
          Atlanta apartment pages and post about moving. It needs one thing first — a first
          pull of who follows those accounts. Nothing has been pulled, so there is nothing
          to show.
        </Typography>
      </SocialsCard>

      <DismissDialog
        open={Boolean(dismissing)}
        postTitle={dismissing ? titleOf(dismissing) : ""}
        onCancel={() => setDismissing(null)}
        onConfirm={(reason) => {
          if (dismissing) {
            const draft = draftReply(dismissing);
            onAction({
              action: "dismissed",
              leadId: dismissing.id,
              finalText: drafts[dismissing.id] ?? draft.text,
              suggestedText: draft.text,
              dismissReason: reason,
            });
          }
          setDismissing(null);
        }}
      />
    </Stack>
  );
};

export default EngageTab;
